import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Settings, Globe, Server, Link2, Save, Loader2, Shield,
  Eye, EyeOff, AlertTriangle, Check, Clock, User, RefreshCw, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [environment, setEnvironment] = useState('devnet');
  const [liveApiUrl, setLiveApiUrl] = useState('');
  const [mainnetRpc, setMainnetRpc] = useState('');
  const [showApiUrl, setShowApiUrl] = useState(false);
  const [showRpc, setShowRpc] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/config`);
      setConfig(data);
      setEnvironment(data.environment || 'devnet');
      setLiveApiUrl(data.live_api_url || '');
      setMainnetRpc(data.mainnet_rpc_endpoint || '');
      setDirty(false);
    } catch (err) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/config`, {
        environment,
        live_api_url: liveApiUrl,
        mainnet_rpc_endpoint: mainnetRpc,
      });
      toast.success(data.message);
      setConfig(data.config);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDemoData = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setResetting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/reset-demo-data`);
      toast.success(data.message);
      setConfirmReset(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset demo data');
    } finally {
      setResetting(false);
    }
  };

  const handleEnvToggle = (checked) => {
    setEnvironment(checked ? 'mainnet' : 'devnet');
    setDirty(true);
  };

  const isMainnet = environment === 'mainnet';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-config-loading">
        <Loader2 className="w-8 h-8 text-[#E8520F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-[#E8520F]" />
            System Configuration
          </h1>
          <p className="text-sm text-[hsl(215,16%,56%)]">Manage environment, endpoints, and network settings</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchConfig} className="border-[hsl(220,16%,18%)]" data-testid="refresh-config-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Reload
        </Button>
      </div>

      {/* Environment Toggle */}
      <Card className={`border shadow-none transition-colors ${isMainnet ? 'border-amber-300 bg-amber-500/10/30' : 'border-[hsl(220,16%,18%)]'}`}
        data-testid="environment-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#E8520F]" /> Environment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-xl bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                isMainnet ? 'bg-amber-100' : 'bg-[#00CED1]/10'
              }`}>
                {isMainnet ? (
                  <AlertTriangle className="w-6 h-6 text-amber-400" />
                ) : (
                  <Shield className="w-6 h-6 text-[#00CED1]" />
                )}
              </div>
              <div>
                <p className="text-lg font-bold text-white font-['Outfit']">
                  {isMainnet ? 'Mainnet' : 'Devnet'}
                </p>
                <p className="text-xs text-[hsl(215,16%,56%)]">
                  {isMainnet
                    ? 'LIVE environment — Real funds and transactions'
                    : 'Test environment — Safe for development and testing'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-medium ${!isMainnet ? 'text-[#00CED1]' : 'text-[hsl(215,16%,45%)]'}`}>Devnet</span>
              <Switch
                checked={isMainnet}
                onCheckedChange={handleEnvToggle}
                data-testid="environment-toggle"
              />
              <span className={`text-xs font-medium ${isMainnet ? 'text-amber-400' : 'text-[hsl(215,16%,45%)]'}`}>Mainnet</span>
            </div>
          </div>

          {isMainnet && (
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2.5"
              data-testid="mainnet-warning">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Mainnet Mode Active</p>
                <p className="text-[11px] text-amber-400 mt-0.5">
                  All transactions will execute on the live blockchain with real funds. Ensure your RPC endpoint and API URL are correctly configured below before deploying bots.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Endpoint Configuration */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none" data-testid="endpoints-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <Server className="w-5 h-5 text-[#6366F1]" /> Endpoint Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Live Backend API URL */}
          <div className="space-y-2">
            <Label className="text-sm text-white font-medium flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5 text-[hsl(215,16%,56%)]" />
              Live Backend API URL
            </Label>
            <p className="text-[11px] text-[hsl(215,16%,45%)] -mt-1">
              The production backend endpoint used when operating in Mainnet mode
            </p>
            <div className="relative">
              <Input
                type={showApiUrl ? 'text' : 'password'}
                value={liveApiUrl}
                onChange={e => { setLiveApiUrl(e.target.value); setDirty(true); }}
                placeholder="https://api.yourdomain.com"
                className="border-[hsl(220,16%,18%)] pr-10 font-mono text-sm h-10"
                data-testid="live-api-url-input"
              />
              <button
                type="button"
                onClick={() => setShowApiUrl(!showApiUrl)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[hsl(215,16%,45%)] hover:text-[hsl(215,16%,56%)] transition-colors"
                data-testid="toggle-api-url-visibility"
              >
                {showApiUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Mainnet RPC Endpoint */}
          <div className="space-y-2">
            <Label className="text-sm text-white font-medium flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-[hsl(215,16%,56%)]" />
              Mainnet RPC Endpoint
            </Label>
            <p className="text-[11px] text-[hsl(215,16%,45%)] -mt-1">
              Solana Mainnet RPC node URL (e.g. Helius, QuickNode, or custom node)
            </p>
            <div className="relative">
              <Input
                type={showRpc ? 'text' : 'password'}
                value={mainnetRpc}
                onChange={e => { setMainnetRpc(e.target.value); setDirty(true); }}
                placeholder="https://mainnet.helius-rpc.com/?api-key=..."
                className="border-[hsl(220,16%,18%)] pr-10 font-mono text-sm h-10"
                data-testid="mainnet-rpc-input"
              />
              <button
                type="button"
                onClick={() => setShowRpc(!showRpc)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[hsl(215,16%,45%)] hover:text-[hsl(215,16%,56%)] transition-colors"
                data-testid="toggle-rpc-visibility"
              >
                {showRpc ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Current active RPC display */}
          <div className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
            <p className="text-[10px] text-[hsl(215,16%,45%)] uppercase tracking-wider mb-1.5">Active RPC</p>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isMainnet && mainnetRpc ? 'bg-amber-500/100' : 'bg-[#00CED1]'}`} />
              <p className="text-xs font-mono text-[hsl(215,16%,56%)] truncate">
                {isMainnet && mainnetRpc
                  ? mainnetRpc.replace(/(api[_-]?key=)[^&]+/gi, '$1••••••')
                  : 'https://api.devnet.solana.com'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Bar */}
      <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
        dirty ? 'border-[#E8520F]/30 bg-[#E8520F]/[0.03]' : 'border-[hsl(220,16%,18%)] bg-[hsl(220,16%,14%)]'
      }`} data-testid="save-bar">
        <div className="flex items-center gap-3">
          {config?.updated_at ? (
            <div className="flex items-center gap-1.5 text-xs text-[hsl(215,16%,45%)]">
              <Clock className="w-3 h-3" />
              Last saved: {new Date(config.updated_at).toLocaleString()}
              {config.updated_by && (
                <span className="flex items-center gap-1 ml-2">
                  <User className="w-3 h-3" /> {config.updated_by}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-[hsl(215,16%,45%)]">No configuration saved yet</p>
          )}
          {dirty && (
            <span className="text-xs font-medium text-[#E8520F] flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#E8520F]" />
              Unsaved changes
            </span>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          className={`${dirty ? 'bg-[#E8520F] hover:bg-[#C9440C]' : 'bg-[#94A3B8]'} text-white transition-colors`}
          data-testid="save-config-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : dirty ? (
            <Save className="w-4 h-4 mr-2" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Saving...' : dirty ? 'Save Configuration' : 'Saved'}
        </Button>
      </div>

      {/* Reset Demo Data */}
      <Card className="border-red-500/30 shadow-none" data-testid="reset-demo-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-500" /> Reset Demo Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[hsl(215,16%,56%)] mb-3">
            Wipes all bot data, earnings, wallet balances, ZK deposits, and withdrawal records. This cannot be undone.
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant={confirmReset ? 'destructive' : 'outline'}
              onClick={handleResetDemoData}
              disabled={resetting}
              className={confirmReset ? '' : 'border-red-500/30 text-red-400 hover:bg-red-500/10'}
              data-testid="reset-demo-btn"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {resetting ? 'Resetting...' : confirmReset ? 'Confirm Reset — Click Again' : 'Reset All Demo Data'}
            </Button>
            {confirmReset && (
              <Button variant="ghost" size="sm" onClick={() => setConfirmReset(false)} className="text-[hsl(215,16%,56%)]">
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
